import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Download, Save, X, Type, Palette, Mic, Lock, Crown } from 'lucide-react';
import { canDownloadQuotecard, getEffectiveTier, TIERS } from '../utils/tierUtils';

const QuoteCardGenerator = ({ initialQuote, episodeTitle, guestName, episodeId, onSave, onCancel, user }) => {
  const canvasRef = useRef(null);
  const [quote, setQuote] = useState(initialQuote || "YOUR ONLY\nLIMIT IS\nYOUR MIND");
  const [author, setAuthor] = useState("EchoKit");
  const [selectedTemplate, setSelectedTemplate] = useState('clouds');
  const [fontSize, setFontSize] = useState(72);
  const [saving, setSaving] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Background templates with different styles
  const templates = {
    clouds: {
      name: "Sky Dreams",
      gradient: ["#5a7d8c", "#8ba9b8", "#b8cdd6"],
      overlay: true,
      textColor: "#2d4a3e"
    },
    sunset: {
      name: "Golden Hour",
      gradient: ["#ff6b6b", "#feca57", "#ff9ff3"],
      overlay: false,
      textColor: "#ffffff"
    },
    ocean: {
      name: "Deep Blue",
      gradient: ["#1e3a5f", "#2e5984", "#4a7ba7"],
      overlay: false,
      textColor: "#ffffff"
    },
    forest: {
      name: "Nature",
      gradient: ["#2d5016", "#4a7c2c", "#6ba53d"],
      overlay: false,
      textColor: "#ffffff"
    },
    minimal: {
      name: "Clean White",
      gradient: ["#f8f9fa", "#ffffff"],
      overlay: false,
      textColor: "#1a1a1a"
    },
    dark: {
      name: "Midnight",
      gradient: ["#1a1a1a", "#2d2d2d", "#404040"],
      overlay: false,
      textColor: "#ffffff"
    },
    purple: {
      name: "Royal Purple",
      gradient: ["#6c5ce7", "#a29bfe", "#dfe6e9"],
      overlay: false,
      textColor: "#2d3436"
    },
    coral: {
      name: "Coral Reef",
      gradient: ["#fd79a8", "#fdcb6e", "#fab1a0"],
      overlay: false,
      textColor: "#2d3436"
    },
    rose: {
      name: "Rose Gold",
      gradient: ["#f093fb", "#f5576c"],
      overlay: false,
      textColor: "#ffffff"
    },
    mint: {
      name: "Mint Fresh",
      gradient: ["#00f5a0", "#00d9f5"],
      overlay: false,
      textColor: "#1a1a1a"
    },
    lavender: {
      name: "Lavender",
      gradient: ["#a8edea", "#fed6e3"],
      overlay: false,
      textColor: "#2d3436"
    },
    amber: {
      name: "Amber Glow",
      gradient: ["#f6d365", "#fda085"],
      overlay: false,
      textColor: "#2d3436"
    },
    teal: {
      name: "Ocean Teal",
      gradient: ["#0ba360", "#3cba92"],
      overlay: false,
      textColor: "#ffffff"
    },
    slate: {
      name: "Slate Blue",
      gradient: ["#6a11cb", "#2575fc"],
      overlay: false,
      textColor: "#ffffff"
    },
    peach: {
      name: "Peach Sunset",
      gradient: ["#fa709a", "#fee140"],
      overlay: false,
      textColor: "#2d3436"
    },
    emerald: {
      name: "Emerald",
      gradient: ["#10b981", "#059669", "#047857"],
      overlay: false,
      textColor: "#ffffff"
    },
    violet: {
      name: "Violet",
      gradient: ["#667eea", "#764ba2"],
      overlay: false,
      textColor: "#ffffff"
    },
    orange: {
      name: "Orange Crush",
      gradient: ["#f09819", "#edde5d"],
      overlay: false,
      textColor: "#2d3436"
    },
    pink: {
      name: "Pink Dreams",
      gradient: ["#fbc2eb", "#a6c1ee"],
      overlay: false,
      textColor: "#2d3436"
    },
    cyan: {
      name: "Cyan",
      gradient: ["#00c9ff", "#92fe9d"],
      overlay: false,
      textColor: "#1a1a1a"
    },
    indigo: {
      name: "Indigo",
      gradient: ["#4facfe", "#00f2fe"],
      overlay: false,
      textColor: "#ffffff"
    },
    red: {
      name: "Red Passion",
      gradient: ["#fa709a", "#fee140"],
      overlay: false,
      textColor: "#ffffff"
    },
    blue: {
      name: "Blue Horizon",
      gradient: ["#4facfe", "#00f2fe"],
      overlay: false,
      textColor: "#ffffff"
    },
    green: {
      name: "Green Valley",
      gradient: ["#84fab0", "#8fd3f4"],
      overlay: false,
      textColor: "#2d3436"
    },
    bronze: {
      name: "Bronze",
      gradient: ["#d4af37", "#f4d03f"],
      overlay: false,
      textColor: "#2d3436"
    },
    twilight: {
      name: "Twilight",
      gradient: ["#2c3e50", "#3498db"],
      overlay: false,
      textColor: "#ffffff"
    },
    fire: {
      name: "Fire",
      gradient: ["#f12711", "#f5af19"],
      overlay: false,
      textColor: "#ffffff"
    },
    cool: {
      name: "Cool",
      gradient: ["#2193b0", "#6dd5ed"],
      overlay: false,
      textColor: "#ffffff"
    },
    warm: {
      name: "Warm",
      gradient: ["#ffeaa7", "#fab1a0"],
      overlay: false,
      textColor: "#2d3436"
    },
    sky: {
      name: "Sky",
      gradient: ["#2980b9", "#6bb6ff"],
      overlay: false,
      textColor: "#ffffff"
    },
    sunset2: {
      name: "Sunset 2",
      gradient: ["#ee0979", "#ff6a00"],
      overlay: false,
      textColor: "#ffffff"
    },
    midnight: {
      name: "Midnight Blue",
      gradient: ["#232526", "#414345"],
      overlay: false,
      textColor: "#ffffff"
    },
    tropical: {
      name: "Tropical",
      gradient: ["#ffecd2", "#fcb69f"],
      overlay: false,
      textColor: "#2d3436"
    },
    space: {
      name: "Space",
      gradient: ["#0f0c29", "#302b63", "#24243e"],
      overlay: false,
      textColor: "#ffffff"
    },
    lemon: {
      name: "Lemon",
      gradient: ["#ffeaa7", "#fdcb6e"],
      overlay: false,
      textColor: "#2d3436"
    },
    grape: {
      name: "Grape",
      gradient: ["#a8c0ff", "#3f2b96"],
      overlay: false,
      textColor: "#ffffff"
    },
    cherry: {
      name: "Cherry",
      gradient: ["#eb3349", "#f45c43"],
      overlay: false,
      textColor: "#ffffff"
    },
    lime: {
      name: "Lime",
      gradient: ["#c2e59c", "#64b3f4"],
      overlay: false,
      textColor: "#2d3436"
    }
  };


  const drawQuoteCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = 1080;
    const height = 1080;
    
    canvas.width = width;
    canvas.height = height;

    const template = templates[selectedTemplate];

    // Create gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    template.gradient.forEach((color, index) => {
      gradient.addColorStop(index / (template.gradient.length - 1), color);
    });
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add cloud-like overlay for certain templates
    if (template.overlay) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.ellipse(width * 0.7, height * 0.4, 400, 200, 0, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.beginPath();
      ctx.ellipse(width * 0.3, height * 0.6, 350, 180, 0, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.beginPath();
      ctx.ellipse(width * 0.5, height * 0.5, 300, 150, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Add subtle texture
    for (let i = 0; i < 1000; i++) {
      ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.03})`;
      ctx.fillRect(
        Math.random() * width,
        Math.random() * height,
        2,
        2
      );
    }

    // Draw quote text with automatic word wrapping
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${fontSize}px Georgia, serif`;
    ctx.fillStyle = template.textColor;
    
    // Auto-wrap text to fit canvas width
    const maxWidth = width - 200; // Leave margins on both sides
    const words = quote.split(/\s+/).filter(word => word.length > 0);
    const wrappedLines = [];
    let currentLine = [];
    
    words.forEach(word => {
      const testLine = currentLine.length > 0 
        ? currentLine.join(' ') + ' ' + word 
        : word;
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine.length > 0) {
        // Current line is full, start a new line
        wrappedLines.push(currentLine.join(' '));
        currentLine = [word];
      } else {
        // Add word to current line
        currentLine.push(word);
      }
    });
    
    // Add the last line if it has content
    if (currentLine.length > 0) {
      wrappedLines.push(currentLine.join(' '));
    }
    
    // If no words, use the original quote (handles empty or special cases)
    const lines = wrappedLines.length > 0 ? wrappedLines : [quote];
    
    const lineHeight = fontSize * 1.2;
    const totalHeight = lines.length * lineHeight;
    const startY = (height - totalHeight) / 2 + fontSize;
    
    lines.forEach((line, index) => {
      ctx.fillText(line, width / 2, startY + index * lineHeight);
    });

    // Draw podcast name at top
    ctx.font = `bold 36px Arial, sans-serif`;  // Increased from 12px to 32px for better visibility
    ctx.fillStyle = template.textColor;
    ctx.globalAlpha = 0.8;  // Slightly increased opacity for better visibility
    ctx.fillText(author.toUpperCase(), width / 2, 80);
    ctx.globalAlpha = 1;
  };

  const downloadCard = async () => {
    // Check if user can download quote card
    if (!canDownloadQuotecard(user)) {
      setShowUpgradeModal(true);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      // Track usage for free users
      if (getEffectiveTier(user) === TIERS.FREE) {
        await axios.post('/api/track-usage', { type: 'quotecard' });
      }

      const link = document.createElement('a');
      link.download = 'echokit-quote.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error tracking usage:', error);
      // Still allow download even if tracking fails
      const link = document.createElement('a');
      link.download = 'echokit-quote.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  const saveCard = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setSaving(true);
    try {
      // Get episode_id from props or storage
      const currentEpisodeId = episodeId || 
                               (window.reviewData && window.reviewData.episode_id) ||
                               localStorage.getItem('current_episode_id');
      
      if (!currentEpisodeId) {
        alert('Episode ID not found. Please go back and try again.');
        setSaving(false);
        return;
      }

      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        const formData = new FormData();
        formData.append('quote_card', blob, 'quote_card.png');
        formData.append('quote', quote);
        formData.append('episode_id', currentEpisodeId);
        formData.append('template', selectedTemplate);
        
        const response = await axios.post('/api/save-quote-card', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (onSave) {
          onSave(response.data.quote_card_path, response.data.quote_card_filename);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error saving quote card:', error);
      alert('Failed to save quote card. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (initialQuote) {
      setQuote(initialQuote);
    }
  }, [initialQuote]);

  useEffect(() => {
    drawQuoteCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, author, selectedTemplate, fontSize]);

  return (
    <motion.div 
      className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800/50 rounded-2xl p-6 mb-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Palette className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-zinc-100">Customize Quote Card</h2>
            <p className="text-sm text-zinc-400">Create shareable graphics for social media</p>
          </div>
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Quote Card Preview, Podcast Name, Quote Text (Fixed, not scrollable) */}
        <div className="flex flex-col gap-4">
          {/* Canvas Preview */}
          <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800/50">
            <div className="aspect-square bg-zinc-950 rounded-xl overflow-hidden mb-4 flex items-center justify-center">
              <canvas
                ref={canvasRef}
                className="max-w-full max-h-full"
                style={{ imageRendering: 'auto' }}
              />
            </div>
            
            <div className="flex gap-3">
              <motion.button
                onClick={downloadCard}
                disabled={!canDownloadQuotecard(user)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium border transition-all ${
                  canDownloadQuotecard(user)
                    ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border-zinc-700 hover:border-zinc-600'
                    : 'bg-zinc-900 text-zinc-500 border-zinc-800 cursor-not-allowed'
                }`}
                whileHover={canDownloadQuotecard(user) ? { scale: 1.02 } : {}}
                whileTap={canDownloadQuotecard(user) ? { scale: 0.98 } : {}}
              >
                {canDownloadQuotecard(user) ? (
                  <Download className="w-4 h-4" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                {canDownloadQuotecard(user) ? 'Download' : 'Limit Reached'}
              </motion.button>
              <motion.button
                onClick={saveCard}
                disabled={saving}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all ${
                  saving 
                    ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed' 
                    : 'bg-emerald-500 hover:bg-emerald-400 text-zinc-950 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40'
                }`}
                whileHover={!saving ? { scale: 1.02 } : {}}
                whileTap={!saving ? { scale: 0.98 } : {}}
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save'}
              </motion.button>
            </div>
          </div>

          {/* Podcast Name */}
          <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
            <div className="flex items-center gap-2 mb-3">
              <Mic className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-medium text-zinc-100">Podcast Name</h3>
            </div>
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-3 text-zinc-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
              placeholder="Your podcast name"
            />
          </div>

          {/* Quote Input */}
          <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
            <div className="flex items-center gap-2 mb-3">
              <Type className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-medium text-zinc-100">Quote Text</h3>
            </div>
            
            <textarea
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-3 text-zinc-100 text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-all"
              style={{ fontFamily: 'Georgia, serif' }}
              placeholder="Enter your quote..."
            />
            <p className="text-xs text-zinc-500 mt-2">
              Text will automatically wrap to fit the quote card
            </p>
          </div>
        </div>

        {/* Right Column - Background Style and Font Size (Scrollable) */}
        <div className="flex flex-col gap-4 max-h-[calc(100vh-12rem)] overflow-y-auto pr-2">
          {/* Template Selection */}
          <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
            <div className="flex items-center gap-2 mb-3">
              <Palette className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-medium text-zinc-100">Background Style</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(templates).map(([key, template]) => (
                <motion.button
                  key={key}
                  onClick={() => setSelectedTemplate(key)}
                  className={`p-3 rounded-lg text-xs font-medium transition-all ${
                    selectedTemplate === key 
                      ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-zinc-900' 
                      : 'hover:scale-105'
                  }`}
                  style={{
                    background: `linear-gradient(180deg, ${template.gradient.join(', ')})`,
                    color: template.textColor === '#ffffff' ? '#ffffff' : '#1a1a1a'
                  }}
                  whileHover={{ scale: selectedTemplate === key ? 1 : 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {template.name}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Font Size */}
          <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-800/50">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-medium text-zinc-100">Font Size</h3>
              </div>
              <span className="text-sm font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                {fontSize}px
              </span>
            </div>
            <input
              type="range"
              min="48"
              max="120"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value))}
              className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
            />
            <div className="flex justify-between text-xs text-zinc-500 mt-1">
              <span>48px</span>
              <span>120px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80 backdrop-blur-sm"
          onClick={() => setShowUpgradeModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-zinc-900 rounded-2xl p-6 max-w-md mx-4 border border-zinc-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Crown className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-zinc-100">Upgrade to Pro</h3>
                <p className="text-sm text-zinc-400">Unlock unlimited quote card downloads</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Unlimited quote card downloads</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Promotion kit emails</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-300">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span>Priority support</span>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-medium transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  window.location.href = '/pricing';
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold transition-colors"
              >
                Upgrade Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default QuoteCardGenerator;

